import React, { useCallback, useMemo } from "react";
import moment from "moment-timezone";
import CommunityEditableGrid from "../CommunityEditableGrid";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import cf from "../../services/codeformatter";
import { INCOTERMS, MEASUREMENT_SYSTEM_TYPE, PACKAGE_TYPE } from "../../services/flitEnum";
import { isValidDate } from "./ConsolUIHelper";
import "./ConsolEditableGrid.css";

const VALID_CURRENCIES = [
  "USD", "EUR", "GBP", "JPY", "CNY", "KRW", "CAD", "AUD", "CHF", "HKD", "SGD",
  "SEK", "NOK", "DKK", "INR", "BRL", "RUB", "MXN", "ZAR", "TRY", "NZD", "THB",
  "MYR", "IDR", "PHP", "VND",
];

const GRID_MAX_HEIGHT = 600;
const GRID_MIN_HEIGHT = 108;
const GRID_HEADER_HEIGHT = 52;
const GRID_ROW_HEIGHT = 26;
const GRID_SCROLLBAR_HEIGHT = 18;
const GRID_DEFAULT_COL_DEF = {
  resizable: true,
  sortable: false,
  wrapHeaderText: true,
  autoHeaderHeight: false,
};

export const CONSOL_ADVANCED_FIELDS = [
  { name: "incoterms", label: "INCOTERMS", type: "enum", options: Object.values(INCOTERMS), validationId: "VALIDATION_INCOTERMS" },
  { name: "houseBillNo", label: "HBL", type: "text" },
  { name: "totalWeight", label: "ACTUAL_WEIGHT_IN_KG", type: "number" },
  { name: "totalVolume", label: "ACTUAL_VOLUME_IN_CBM", type: "number" },
  { name: "volumeMemo", label: "VOLUME_MEMO", type: "text" },
  { name: "totalPackageType", label: "PACKAGE_TYPE", type: "enum", options: Object.values(PACKAGE_TYPE), validationId: "VALIDATION_PACKAGE_TYPE" },
  { name: "totalPackages", label: "TOTAL_PACKAGES", type: "number" },
  { name: "isPickup", label: "IS_PICKUP_DESC", type: "boolean" },
  { name: "originDropOffOn", label: "ORIGIN_DROPOFF_ON", type: "date" },
  { name: "deliveryObjective", label: "DELIVERY_OBJECTIVE", type: "text" },
  { name: "note", label: "CONSOL_NOTE", type: "text" },
];

export const CONSOL_EXTRA_FIELD_GROUPS = [
  [
    { name: "quotedVolume", label: "QUOTED_VOLUME", type: "number" },
    { name: "quotedRate", label: "QUOTED_RATE", type: "number" },
    { name: "quotedRateCurrency", label: "QUOTED_RATE_CURRENCY", type: "currency" },
    { name: "trukingCost", label: "TRUCKING_COST", type: "number" },
    { name: "truckingCostCurrency", label: "TRUCKING_COST_CURRENCY", type: "currency" },
    { name: "truckingRevenue", label: "TRUCKING_REVENUE", type: "number" },
    { name: "truckingRevenueCurrency", label: "TRUCKING_REVENUE_CURRENCY", type: "currency" },
    { name: "customsCost", label: "CUSTOMS_COST", type: "number" },
    { name: "customsCostCurrency", label: "CUSTOMS_COST_CURRENCY", type: "currency" },
    { name: "customsRevenue", label: "CUSTOMS_REVENUE", type: "number" },
    { name: "customsRevenueCurrency", label: "CUSTOMS_REVENUE_CURRENCY", type: "currency" },
    { name: "_3plcost", label: "3PL_COST", type: "number" },
    { name: "_3plcostCurrency", label: "3PL_COST_CURRENCY", type: "currency" },
    { name: "crossDockingCost", label: "CROSS_DOCKING_COST", type: "number" },
    { name: "crossDockingCostCurrency", label: "CROSS_DOCKING_COST_CURRENCY", type: "currency" },
    { name: "fsvpps", label: "FSVPPS", type: "number" },
    { name: "fsvppscurrency", label: "FSVPPS_CURRENCY", type: "currency" },
    { name: "memo", label: "MEMO", type: "text" },
  ],
  [
    { name: "fdaFilingFee", label: "FDA_FILING_FEE", type: "number" },
    { name: "fdaFilingFeeCurrency", label: "FDA_FILING_FEE_CURRENCY", type: "currency" },
    { name: "fdaPriorNoticeFee", label: "FDA_PRIOR_NOTICE_FEE", type: "number" },
    { name: "fdaPriorNoticeFeeCurrency", label: "FDA_PRIOR_NOTICE_FEE_CURRENCY", type: "currency" },
    { name: "ddp", label: "DDP", type: "number" },
    { name: "ddPcurrency", label: "DDP_CURRENCY", type: "currency" },
    { name: "singleBond", label: "SINGLE_BOND", type: "number" },
    { name: "singleBondCurrency", label: "SINGLE_BOND_CURRENCY", type: "currency" },
    { name: "insurance", label: "INSURANCE", type: "number" },
    { name: "insuranceCurrency", label: "INSURANCE_CURRENCY", type: "currency" },
    { name: "isftAbenefit", label: "IS_FTA_BENEFIT", type: "boolean" },
    { name: "isfdAconfirmed", label: "IS_FDA_CONFIRMED", type: "boolean" },
    { name: "fdAmemo", label: "FDA_MEMO", type: "text" },
    { name: "ior", label: "IOR", type: "text" },
  ],
];

export const CONSOL_EXTRA_FIELDS = [].concat(...CONSOL_EXTRA_FIELD_GROUPS);

function formatNumber(value) {
  if (value === null || value === undefined || value === "") return "";
  const number = Number(String(value).replace(/,/g, ""));
  if (Number.isNaN(number)) return value;
  const parts = String(number).split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return parts.join(".");
}

function parseNumber(value) {
  return (value ?? "").toString().replace(/,/g, "").trim();
}

function parseBoolean(value) {
  if (value === null || value === undefined || value === "") return null;
  if (value === true || value === false) return value;
  const normalized = String(value).trim().toUpperCase();
  if (["Y", "YES", "TRUE", "1"].includes(normalized)) return true;
  if (["N", "NO", "FALSE", "0"].includes(normalized)) return false;
  return normalized;
}

function formatBoolean(value) {
  if (value === true) return "Y";
  if (value === false) return "N";
  return value ?? "";
}

function parseDate(value) {
  const normalized = (value ?? "").toString().trim();
  if (!normalized) return null;
  return isValidDate(normalized) ? moment(normalized).format("YYYY-MM-DD") : normalized;
}

export function getConsolGridError(field, value, intl) {
  switch (field.type) {
    case "number":
      return value !== "" && value !== null && value !== undefined && Number.isNaN(Number(value))
        ? intl.formatMessage({ id: "VALIDATION_NUMBER" }) : null;
    case "currency":
      return value && !VALID_CURRENCIES.includes(value)
        ? intl.formatMessage({ id: "VALIDATION_CURRENCY" }) : null;
    case "enum":
      return value && !field.options.includes(value)
        ? intl.formatMessage({ id: field.validationId }) : null;
    case "boolean":
      return value === null || value === undefined || value === "" || value === true || value === false
        ? null : intl.formatMessage({ id: "VALIDATION_YES_NO" });
    case "date":
      return value && !isValidDate(value)
        ? intl.formatMessage({ id: "VALIDATION_DATE" }) : null;
    default:
      return null;
  }
}

export function getConsolGridErrors(orders, fields, extra, intl) {
  const errors = [];
  orders.forEach(order => {
    fields.forEach(field => {
      const value = extra ? order.consolExtraTemp?.[field.name] : order[field.name];
      const message = getConsolGridError(field, value, intl);
      if (message) {
        errors.push({
          key: order.id + ":" + (extra ? "extra:" : "advanced:") + field.name,
          label: "FLIT-C-" + order.id + " · "
            + intl.formatMessage({ id: field.label }) + " : " + message,
        });
      }
    });
  });
  return errors;
}

export function hasConsolGridErrors(orders, fields, extra, intl) {
  return getConsolGridErrors(orders, fields, extra, intl).length > 0;
}

function normalizeValue(field, value) {
  if (field.type === "number") return parseNumber(value);
  if (field.type === "currency" || field.type === "enum") {
    return value ? String(value).trim().toUpperCase() : "";
  }
  if (field.type === "boolean") return parseBoolean(value);
  if (field.type === "date") return parseDate(value);
  return value ?? "";
}

function fieldColumn(field, intl) {
  const width = field.type === "currency" ? 82
    : field.type === "boolean" ? 78
      : field.type === "number" ? 90
        : field.type === "date" ? 105
          : 120;
  const column = {
    field: field.name,
    headerName: intl.formatMessage({ id: field.label }),
    width,
    minWidth: width,
    editable: params => !params.data?.isSummary,
    valueParser: params => normalizeValue(field, params.newValue),
    valueFormatter: params => field.type === "number"
      ? formatNumber(params.value)
      : field.type === "boolean"
        ? formatBoolean(params.value)
        : params.value ?? "",
  };
  if (field.type === "currency") {
    column.cellEditor = "agSelectCellEditor";
    column.cellEditorParams = { values: ["", ...VALID_CURRENCIES] };
  } else if (field.type === "enum") {
    column.cellEditor = "agSelectCellEditor";
    column.cellEditorParams = { values: ["", ...field.options] };
  } else if (field.type === "boolean") {
    column.cellEditor = "agSelectCellEditor";
    column.cellEditorParams = { values: [null, true, false] };
  }
  return column;
}

function readOnlyColumn(field, label, intl, width) {
  return {
    field,
    headerName: intl.formatMessage({ id: label }),
    editable: false,
    width,
    minWidth: width,
    initialPinned: "left",
  };
}

function identityRow(order, intl) {
  const request = order.activity?.quoteRequest?.[0];
  return {
    id: order.id,
    formikIndex: order.formikIndex,
    orderIdLabel: `FLIT-C-${order.id}`,
    statusLabel: cf.format("ConsolStatus", order.status, intl.locale),
    businessEntity: request?.submittedBy?.businessEntity?.legalName ?? "",
    description: request?.description ?? "",
    submittedBy: `${request?.submittedBy?.firstName ?? ""} ${request?.submittedBy?.lastName ?? ""}`.trim(),
    requestedWeight: request?.totalWeight === null || request?.totalWeight === undefined
      ? "" : `${request.totalWeight} ${request.measurementSystem === MEASUREMENT_SYSTEM_TYPE.METRIC ? "kg" : "lb"}`,
    requestedVolume: request?.totalVolume === null || request?.totalVolume === undefined
      ? "" : `${request.totalVolume} ${request.measurementSystem === MEASUREMENT_SYSTEM_TYPE.METRIC ? "cbm" : "cbf"}`,
  };
}

export default function ConsolEditableGrid({
  orders,
  allOrders,
  touched,
  setFieldValue,
  setFieldTouched,
  intl,
  fields,
  extra = false,
  showSummary = false,
}) {
  const rows = useMemo(() => {
    const orderRows = orders.map(order => {
      const row = identityRow(order, intl);
      fields.forEach(field => {
        row[field.name] = extra ? order.consolExtraTemp?.[field.name] ?? "" : order[field.name] ?? "";
      });
      return row;
    });

    if (!showSummary) return orderRows;

    const totalWeight = orders.reduce((sum, order) => sum + (Number(order.totalWeight) || 0), 0);
    const totalVolume = orders.reduce((sum, order) => sum + (Number(order.totalVolume) || 0), 0);
    const packagesByType = orders.reduce((result, order) => {
      if (order.totalPackageType && order.totalPackages) {
        result[order.totalPackageType] = (result[order.totalPackageType] || 0) + (Number(order.totalPackages) || 0);
      }
      return result;
    }, {});
    return orderRows.concat([
      {
        id: "summary-total",
        isSummary: true,
        orderIdLabel: intl.formatMessage({ id: "TOTAL" }),
        totalWeight: `${totalWeight.toFixed(2)} kg`,
        totalVolume: `${totalVolume.toFixed(2)} cbm`,
      },
      ...Object.entries(packagesByType).map(([packageType, count]) => ({
        id: `summary-package-${packageType}`,
        isSummary: true,
        orderIdLabel: `${intl.formatMessage({ id: "TOTAL" })} - ${cf.format("PackageType", packageType, intl.locale)}`,
        totalPackageType: packageType,
        totalPackages: count,
      })),
    ]);
  }, [orders, fields, extra, showSummary, intl]);

  const cellClass = useCallback(params => {
    if (params.data?.isSummary) return "consol-ag-summary";
    const field = fields.find(item => item.name === params.colDef.field);
    if (!field) return undefined;
    const order = allOrders[params.data.formikIndex];
    const value = extra ? order?.consolExtraTemp?.[field.name] : order?.[field.name];
    if (getConsolGridError(field, value, intl)) return "ag-cell-invalid";
    const marker = extra
      ? touched.orders?.[params.data.formikIndex]?.consolExtraTemp?.[field.name]
      : touched.orders?.[params.data.formikIndex]?.[field.name];
    return marker === 1 ? "ag-cell-modified" : undefined;
  }, [allOrders, touched, fields, extra, intl]);

  const columns = useMemo(() => {
    const identityColumns = [
      {
        ...readOnlyColumn("orderIdLabel", "ORDER_ID", intl, 135),
        cellClass: "community-order-id-cell",
      },
      readOnlyColumn("statusLabel", "STATUS", intl, 105),
      readOnlyColumn("businessEntity", "BUSINESS_ENTITY", intl, 140),
    ];
    if (!extra) {
      identityColumns.push(
        { ...readOnlyColumn("description", "DESCRIPTION", intl, 150), initialPinned: null },
        { ...readOnlyColumn("submittedBy", "SUBMITTED_BY", intl, 120), initialPinned: null },
        { ...readOnlyColumn("requestedWeight", "REQUESTED_WEIGHT", intl, 115), initialPinned: null },
        { ...readOnlyColumn("requestedVolume", "REQUESTED_VOLUME", intl, 115), initialPinned: null }
      );
    }
    return identityColumns.concat(fields.map(field => ({
      ...fieldColumn(field, intl),
      cellClass,
      tooltipValueGetter: params => getConsolGridError(field, params.value, intl),
    })));
  }, [fields, extra, intl, cellClass]);

  const handleCellValueChanged = useCallback(event => {
    if (event.data.isSummary) return;
    const field = fields.find(item => item.name === event.colDef.field);
    const order = allOrders[event.data.formikIndex];
    if (!field || !order) return;
    const nextValue = normalizeValue(field, event.newValue);
    const currentValue = extra ? order.consolExtraTemp?.[field.name] : order[field.name];
    if ((currentValue ?? "") === (nextValue ?? "")) return;
    const fieldPath = extra
      ? `orders.${event.data.formikIndex}.consolExtraTemp.${field.name}`
      : `orders.${event.data.formikIndex}.${field.name}`;
    setFieldValue(fieldPath, nextValue, false);
    setFieldTouched(fieldPath, getConsolGridError(field, nextValue, intl) || 1, false);
    setFieldValue(
      `orders.${event.data.formikIndex}.${extra ? "needsUpdateExtras" : "needsUpdate"}`,
      true,
      false
    );
  }, [allOrders, fields, extra, intl, setFieldTouched, setFieldValue]);

  const gridHeight = Math.min(
    GRID_MAX_HEIGHT,
    Math.max(
      GRID_MIN_HEIGHT,
      GRID_HEADER_HEIGHT + GRID_SCROLLBAR_HEIGHT + rows.length * GRID_ROW_HEIGHT
    )
  );

  return (
    <div className="ag-theme-alpine flit-ag-grid consol-ag-grid" style={{ height: gridHeight }}>
      <CommunityEditableGrid
        rowData={rows}
        columnDefs={columns}
        defaultColDef={GRID_DEFAULT_COL_DEF}
        getRowId={params => String(params.data.id)}
        onCellValueChanged={handleCellValueChanged}
        getRowClass={params => params.data?.isSummary ? "consol-ag-summary" : undefined}
        rowHeight={GRID_ROW_HEIGHT}
        headerHeight={GRID_HEADER_HEIGHT}
        stopEditingWhenCellsLoseFocus
        suppressRowClickSelection
        suppressDragLeaveHidesColumns
      />
    </div>
  );
}
